/**
 * CartCheckoutPage.duck.js
 *
 * Data loading and order placement for the basket checkout.
 *
 * Sharetribe's transaction processes buy one listing per transaction: there is
 * no "order with many listings" resource, and each brand is paid through its own
 * Stripe connected account. A basket therefore becomes *one transaction per
 * basket line*, and this duck
 *
 *   1) fetches the real listings behind the basket (never trusting the price,
 *      stock or delivery options cached in localStorage),
 *   2) speculates each line so the shopper sees true totals — item price,
 *      shipping fee, commission — before paying, and
 *   3) places the orders one after another on submit, paid with one card.
 *
 * Step 3 is deliberately sequential. Each transaction has its own Stripe
 * PaymentIntent and 3-D Secure can put a challenge in front of any of them, so
 * doing them in order means at most one challenge is on screen at a time, and a
 * failure stops the run instead of half-charging in parallel. Lines that did go
 * through are reported back so the page can take them out of the basket: a retry
 * must never pay for the same listing twice.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { initiatePrivileged } from '../../util/api';
import { denormalisedResponseEntities, ensureStripeCustomer } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { displayDeliveryPickup, displayDeliveryShipping } from '../../util/configHelpers';
import { LISTING_STATE_CLOSED } from '../../util/types';
import {
  getProcess,
  isPurchaseProcessAlias,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { getVariantCombinationKey, getVariantProtectedDataMaybe } from '../../util/productVariants';
import { getCombinationStock, getVariantStockMap } from '../../util/variantStock';
import { lineIdOf } from '../../ducks/cart.duck';
import { setCurrentUserHasOrders } from '../../ducks/user.duck';
import { confirmCardPayment } from '../../ducks/stripe.duck';
import { savePaymentMethod } from '../../ducks/paymentMethods.duck';

const { UUID } = sdkTypes;

// Why a basket line can't be checked out here.
export const UNAVAILABLE_NOT_FOUND = 'notFound';
export const UNAVAILABLE_CLOSED = 'closed';
export const UNAVAILABLE_OUT_OF_STOCK = 'outOfStock';
// The product is still on sale, just not in the size/colour that was chosen.
export const UNAVAILABLE_VARIANT_OUT_OF_STOCK = 'variantOutOfStock';
export const UNAVAILABLE_OWN_LISTING = 'ownListing';
export const UNAVAILABLE_UNSUPPORTED_PROCESS = 'unsupportedProcess';
export const UNAVAILABLE_NO_DELIVERY_METHOD = 'noDeliveryMethod';
export const UNAVAILABLE_TOO_MANY = 'tooMany';

// Each line is a separate API call, so a basket of hundreds of distinct listings
// would mean a very long request chain. Baskets that size are not a realistic
// shopping session, and the cap keeps the page predictable.
export const MAX_CART_CHECKOUT_LINES = 20;

// ================ Helpers ================ //

/**
 * Resolve the delivery method for a listing. ProductOrderForm lets the shopper
 * choose when a brand offers both shipping and pickup; the basket collects one
 * shipping address for the whole order, so shipping wins here when it's offered.
 *
 * @param {Object} listing listing entity
 * @param {Object} config app configuration (for the listing type config)
 * @returns {String|null} 'shipping', 'pickup', 'none', or null if neither is set
 */
export const resolveDeliveryMethod = (listing, config) => {
  const { listingType, pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};
  const listingTypeConfig = config?.listing?.listingTypes?.find(
    conf => conf.listingType === listingType
  );
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);

  if (!displayShipping && !displayPickup) {
    // This listing type does not collect a delivery method at all.
    return 'none';
  }
  const canShip = shippingEnabled && displayShipping;
  const canPickUp = pickupEnabled && displayPickup;

  return canShip ? 'shipping' : canPickUp ? 'pickup' : null;
};

/**
 * Extra protected data written on every transaction for a line, mirroring what
 * the single-listing checkout stores (the unit type comes from the listing's
 * own public data, since the listing type config might have changed since).
 *
 * @param {Object} listing listing entity
 * @param {Object} config app configuration
 * @returns {Object} transaction type data
 */
const getLineTransactionTypeData = (listing, config) => {
  const { listingType, unitType } = listing?.attributes?.publicData || {};
  const listingTypeConfig = config?.listing?.listingTypes?.find(
    conf => conf.listingType === listingType
  );
  const { process, alias, unitType: unitTypeInConfig, ...rest } =
    listingTypeConfig?.transactionType || {};
  return unitType ? { unitType, ...rest } : {};
};

/**
 * Turn the basket (localStorage snapshots) and the listings just fetched from
 * the API into checkout lines, flagging the ones that can't be ordered.
 *
 * @param {Array} cartItems basket items ({ lineId, listingId, quantity, title, productVariants })
 * @param {Array} listings listing entities returned by the API
 * @param {Object} params { config, currentUserId }
 * @returns {Object} { lines, unavailableLines }
 */
export const buildCartCheckoutLines = (cartItems = [], listings = [], params = {}) => {
  const { config, currentUserId } = params;
  const lines = [];
  const unavailableLines = [];

  // Two variants of one product are two lines but share one pool of stock, so
  // what has to fit is the basket's total for that listing.
  const quantityByListing = cartItems.reduce(
    (acc, item) => ({ ...acc, [item.listingId]: (acc[item.listingId] || 0) + item.quantity }),
    {}
  );

  cartItems.forEach((item, index) => {
    const lineId = lineIdOf(item);
    const listing = listings.find(l => l.id?.uuid === item.listingId);
    const title = listing?.attributes?.title || item.title || '';
    const unavailable = reason =>
      unavailableLines.push({
        lineId,
        listingId: item.listingId,
        quantity: item.quantity,
        productVariants: item.productVariants,
        title,
        reason,
      });

    if (index >= MAX_CART_CHECKOUT_LINES) {
      unavailable(UNAVAILABLE_TOO_MANY);
      return;
    }
    if (!listing) {
      // listings.query only returns published listings, so one that has been
      // deleted or unpublished since it was added simply isn't in the response.
      unavailable(UNAVAILABLE_NOT_FOUND);
      return;
    }
    if (listing.attributes.state === LISTING_STATE_CLOSED) {
      unavailable(UNAVAILABLE_CLOSED);
      return;
    }
    if (currentUserId && listing.author?.id?.uuid === currentUserId) {
      unavailable(UNAVAILABLE_OWN_LISTING);
      return;
    }

    const processAlias = listing.attributes.publicData?.transactionProcessAlias;
    if (!processAlias || !isPurchaseProcessAlias(processAlias)) {
      // Bookings and negotiated orders need dates or an agreed price, so they
      // are checked out on their own listing page rather than in a basket.
      unavailable(UNAVAILABLE_UNSUPPORTED_PROCESS);
      return;
    }

    const stock = listing.currentStock?.attributes?.quantity;
    if (typeof stock === 'number' && stock < quantityByListing[item.listingId]) {
      unavailable(UNAVAILABLE_OUT_OF_STOCK);
      return;
    }

    // The product as a whole may still have stock while the exact version in
    // the basket has sold out since it was added — checked against the listing
    // just fetched, not the snapshot taken when it went into the basket.
    const combinationKey = getVariantCombinationKey(item.productVariants);
    const variantStock = combinationKey
      ? getCombinationStock(getVariantStockMap(listing.attributes.publicData), combinationKey)
      : null;
    if (typeof variantStock === 'number' && variantStock < item.quantity) {
      unavailable(UNAVAILABLE_VARIANT_OUT_OF_STOCK);
      return;
    }

    const deliveryMethod = resolveDeliveryMethod(listing, config);
    if (deliveryMethod === null) {
      // The brand offers neither shipping nor collection, so there is no way to
      // price delivery for this line (this only happens with imported listings).
      unavailable(UNAVAILABLE_NO_DELIVERY_METHOD);
      return;
    }

    lines.push({
      lineId,
      listingId: item.listingId,
      quantity: item.quantity,
      productVariants: item.productVariants,
      title,
      listing,
      processAlias,
      deliveryMethod,
      transactionTypeData: getLineTransactionTypeData(listing, config),
    });
  });

  return { lines, unavailableLines };
};

/**
 * Build the Marketplace API params for one line's request-payment transition.
 *
 * Note: deliveryMethod is *not* one of them — like the single-listing checkout,
 * it goes to the client app's server as orderData (so it can price the shipping
 * line item) and onto the transaction as protected data.
 *
 * @param {Object} line checkout line. `line.protectedData` (e.g. the shipping
 * address for a line that is shipped) is written onto the transaction.
 * @param {Object} optionalPaymentParams Stripe payment params for the API
 * @returns {Object} transition params
 */
export const getLineOrderParams = (line, optionalPaymentParams = {}) => {
  const { deliveryMethod, transactionTypeData = {}, protectedData = {}, productVariants } = line;
  const deliveryMethodMaybe = deliveryMethod && deliveryMethod !== 'none' ? { deliveryMethod } : {};
  // Which version of the product was bought, so the brand knows what to pack.
  const productVariantsMaybe = getVariantProtectedDataMaybe(productVariants);

  return {
    listingId: new UUID(line.listingId),
    stockReservationQuantity: line.quantity,
    protectedData: {
      ...transactionTypeData,
      ...deliveryMethodMaybe,
      ...productVariantsMaybe,
      ...protectedData,
    },
    ...optionalPaymentParams,
  };
};

/**
 * Make the (privileged) request-payment transition for one line, speculatively
 * or for real.
 *
 * @param {Object} params { line, orderParams, isSpeculative }
 * @returns {Promise<Object>} the (speculated) transaction entity
 */
const requestPaymentForLine = ({ line, orderParams, isSpeculative }) => {
  const { deliveryMethod, processAlias } = line;
  const process = getProcess(resolveLatestProcessName(processAlias.split('/')[0]));
  const speculativeParamsMaybe = isSpeculative
    ? { cardToken: 'CartCheckoutPage_speculative_card_token' }
    : {};

  return initiatePrivileged({
    isSpeculative,
    orderData: deliveryMethod && deliveryMethod !== 'none' ? { deliveryMethod } : {},
    bodyParams: {
      processAlias,
      transition: process.transitions.REQUEST_PAYMENT,
      params: { ...orderParams, ...speculativeParamsMaybe },
    },
    queryParams: { include: ['provider'], expand: true },
  }).then(response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the request-payment response');
    }
    return entities[0];
  });
};

// A line is still being speculated while it has neither a transaction nor an error.
const hasPendingSpeculation = state =>
  state.lines.some(
    line => !state.speculatedTransactions[lineIdOf(line)] && !state.speculateErrors[lineIdOf(line)]
  );

// ================ Async thunks ================ //

///////////////////////////
// Fetch basket listings //
///////////////////////////
const fetchCartListingsPayloadCreator = (
  { cartItems, config, currentUserId },
  { extra: sdk, rejectWithValue }
) => {
  const ids = cartItems.slice(0, MAX_CART_CHECKOUT_LINES).map(item => item.listingId);
  if (ids.length === 0) {
    return Promise.resolve(buildCartCheckoutLines(cartItems, [], { config, currentUserId }));
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      ids,
      include: ['author', 'images', 'currentStock'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      'limit.images': 1,
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      // NOTE: these listings are deliberately not merged into marketplaceData.
      // Only this page needs them, and merging a partial listing can overwrite
      // image variants that other pages depend on.
      const listings = denormalisedResponseEntities(response);
      return buildCartCheckoutLines(cartItems, listings, { config, currentUserId });
    })
    .catch(e => {
      log.error(e, 'cart-checkout-fetch-listings-failed', { listingIds: ids.join(',') });
      return rejectWithValue(storableError(e));
    });
};

export const fetchCartListingsThunk = createAsyncThunk(
  'CartCheckoutPage/fetchCartListings',
  fetchCartListingsPayloadCreator
);

////////////////////////
// Speculate one line //
////////////////////////
const speculateLinePayloadCreator = ({ line }, { rejectWithValue }) => {
  return requestPaymentForLine({
    line,
    orderParams: getLineOrderParams(line),
    isSpeculative: true,
  })
    .then(transaction => ({ lineId: lineIdOf(line), transaction }))
    .catch(e => {
      log.error(e, 'cart-checkout-speculate-failed', {
        listingId: line.listingId,
        quantity: line.quantity,
      });
      return rejectWithValue({ lineId: lineIdOf(line), error: storableError(e) });
    });
};

export const speculateLineThunk = createAsyncThunk(
  'CartCheckoutPage/speculateLine',
  speculateLinePayloadCreator
);

////////////////////////////////
// Confirm payment for a line //
////////////////////////////////
const confirmLinePaymentPayloadCreator = (
  { transactionId, transitionName },
  { extra: sdk, rejectWithValue }
) => {
  return sdk.transactions
    .transition(
      { id: transactionId, transition: transitionName, params: {} },
      { include: ['provider'], expand: true }
    )
    .then(response => response.data.data)
    .catch(e => {
      log.error(e, 'cart-checkout-confirm-payment-failed', {
        transactionId: transactionId?.uuid,
      });
      return rejectWithValue(storableError(e));
    });
};

export const confirmLinePaymentThunk = createAsyncThunk(
  'CartCheckoutPage/confirmLinePayment',
  confirmLinePaymentPayloadCreator
);

// ================ Slice ================ //

const initialState = {
  fetchLinesInProgress: false,
  fetchLinesError: null,
  lines: [],
  unavailableLines: [],
  speculateInProgress: false,
  speculatedTransactions: {},
  speculateErrors: {},
  placeOrdersInProgress: false,
  placedOrders: [],
  failedOrder: null,
};

const cartCheckoutPageSlice = createSlice({
  name: 'CartCheckoutPage',
  initialState,
  reducers: {
    clearCartCheckoutData: () => ({ ...initialState }),
    placeOrdersStarted: state => {
      state.placeOrdersInProgress = true;
      state.placedOrders = [];
      state.failedOrder = null;
    },
    placeOrdersFinished: state => {
      state.placeOrdersInProgress = false;
    },
    lineOrderSucceeded: (state, action) => {
      state.placedOrders = [...state.placedOrders, action.payload];
    },
    lineOrderFailed: (state, action) => {
      state.failedOrder = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCartListingsThunk.pending, state => {
        state.fetchLinesInProgress = true;
        state.fetchLinesError = null;
        state.speculatedTransactions = {};
        state.speculateErrors = {};
      })
      .addCase(fetchCartListingsThunk.fulfilled, (state, action) => {
        state.fetchLinesInProgress = false;
        state.lines = action.payload.lines;
        state.unavailableLines = action.payload.unavailableLines;
        // Every orderable line is about to be speculated.
        state.speculateInProgress = action.payload.lines.length > 0;
      })
      .addCase(fetchCartListingsThunk.rejected, (state, action) => {
        state.fetchLinesInProgress = false;
        state.fetchLinesError = action.payload;
      })
      .addCase(speculateLineThunk.fulfilled, (state, action) => {
        const { lineId, transaction } = action.payload;
        state.speculatedTransactions = {
          ...state.speculatedTransactions,
          [lineId]: transaction,
        };
        state.speculateErrors = Object.fromEntries(
          Object.entries(state.speculateErrors).filter(([key]) => key !== lineId)
        );
        state.speculateInProgress = hasPendingSpeculation(state);
      })
      .addCase(speculateLineThunk.rejected, (state, action) => {
        const { lineId, error } = action.payload || {};
        if (lineId) {
          state.speculateErrors = { ...state.speculateErrors, [lineId]: error };
        }
        state.speculateInProgress = hasPendingSpeculation(state);
      });
  },
});

const actions = cartCheckoutPageSlice.actions;
export const { clearCartCheckoutData } = actions;

// ================ Thunks ================ //

/**
 * Load everything the basket checkout needs: the real listings behind the
 * basket, and a speculated transaction for each orderable line.
 *
 * @param {Array} cartItems basket items from cart.duck
 * @param {Object} config app configuration
 * @returns {Function} thunk
 */
export const loadCartCheckoutData = (cartItems, config) => (dispatch, getState) => {
  const currentUserId = getState().user?.currentUser?.id?.uuid;

  return dispatch(fetchCartListingsThunk({ cartItems, config, currentUserId }))
    .unwrap()
    .then(({ lines }) =>
      // Speculations are independent of each other, so they run together. Each
      // one records its own outcome, so a single failure isn't fatal here.
      Promise.all(lines.map(line => dispatch(speculateLineThunk({ line }))))
    )
    .catch(() => {
      // The failure is already in state (fetchLinesError) and logged.
    });
};

/**
 * Place an order for every line in the basket, paying for each with the same
 * card, and report what happened.
 *
 * The returned promise always resolves: a failure part-way through is a real
 * outcome the page has to show (some orders placed, one failed), not an
 * exception. `placedOrders` is what the caller must remove from the basket.
 *
 * @param {Object} params
 * @param {Array} params.lines checkout lines to order
 * @param {Object} params.stripe initialized Stripe instance
 * @param {Object} params.card Stripe card element (null when paying with a saved card)
 * @param {Object} params.billingDetails billing details for Stripe
 * @param {String} params.savedPaymentMethodId Stripe payment method id of the saved card
 * @param {Boolean} params.saveCardForLater save the new card to the Stripe customer
 * @param {Object} params.stripeCustomer the current user's Stripe customer entity
 * @returns {Function} thunk resolving to { placedOrders, failedOrder, paymentMethodSaved }
 */
export const placeCartOrders = params => async dispatch => {
  const {
    lines = [],
    stripe,
    card,
    billingDetails,
    savedPaymentMethodId = null,
    saveCardForLater = false,
    stripeCustomer = null,
  } = params;

  const placedOrders = [];
  let failedOrder = null;
  let paymentMethodSaved = !saveCardForLater;
  // The payment method the first order was paid with — the only one that can be
  // attached to the Stripe customer afterwards (see setupPaymentMethodForSaving).
  let firstPaymentMethodId = null;

  dispatch(actions.placeOrdersStarted());

  for (const line of lines) {
    const isFirstLine = line === lines[0];
    try {
      // A card PaymentMethod that isn't attached to a Stripe customer can pay
      // for a single PaymentIntent only, so each line gets a freshly created
      // one from the same card element. A saved card is already attached to the
      // customer, so it can pay for every line and is set on the PaymentIntent
      // by the Marketplace API when the order is initiated.
      let paymentParams = null;
      if (!savedPaymentMethodId) {
        const { paymentMethod, error } = await stripe.createPaymentMethod({
          type: 'card',
          card,
          billing_details: billingDetails,
        });
        if (error) {
          throw error;
        }
        paymentParams = { payment_method: paymentMethod.id };
      }

      // Saving the card is only asked of the first PaymentIntent: Stripe lets a
      // payment method be attached to a customer once, and the shopper only
      // needs one card on file.
      const optionalPaymentParams = savedPaymentMethodId
        ? { paymentMethod: savedPaymentMethodId }
        : saveCardForLater && isFirstLine
        ? { setupPaymentMethodForSaving: true }
        : {};

      const order = await requestPaymentForLine({
        line,
        orderParams: getLineOrderParams(line, optionalPaymentParams),
        isSpeculative: false,
      });
      dispatch(setCurrentUserHasOrders());

      const { stripePaymentIntentClientSecret } =
        order.attributes.protectedData?.stripePaymentIntents?.default || {};
      if (!stripePaymentIntentClientSecret) {
        throw new Error(
          `Missing StripePaymentIntents key in transaction's protectedData. Check that your transaction process is configured to use payment intents.`
        );
      }

      const confirmed = await dispatch(
        confirmCardPayment({
          stripe,
          stripePaymentIntentClientSecret,
          orderId: order.id,
          ...(paymentParams ? { paymentParams } : {}),
        })
      );
      if (isFirstLine) {
        firstPaymentMethodId = confirmed?.paymentIntent?.payment_method || null;
      }

      const process = getProcess(resolveLatestProcessName(line.processAlias.split('/')[0]));
      await dispatch(
        confirmLinePaymentThunk({
          transactionId: order.id,
          transitionName: process.transitions.CONFIRM_PAYMENT,
        })
      ).unwrap();

      const placed = {
        lineId: lineIdOf(line),
        listingId: line.listingId,
        title: line.title,
        orderId: order.id.uuid,
      };
      placedOrders.push(placed);
      dispatch(actions.lineOrderSucceeded(placed));
    } catch (e) {
      log.error(e, 'cart-checkout-order-failed', {
        listingId: line.listingId,
        placedOrderCount: placedOrders.length,
      });
      failedOrder = {
        lineId: lineIdOf(line),
        listingId: line.listingId,
        title: line.title,
        // Stripe errors arrive wrapped; storableError keeps only what we show.
        error: storableError(e?.error || e),
      };
      dispatch(actions.lineOrderFailed(failedOrder));
      // Stop rather than charging for the rest of the basket: whatever went
      // wrong (a declined card, an abandoned 3-D Secure challenge) is likely to
      // affect the remaining lines too.
      break;
    }
  }

  if (saveCardForLater && firstPaymentMethodId) {
    // A card that couldn't be saved is not worth failing the order over — the
    // shopper has paid, and they can add the card in their account settings.
    paymentMethodSaved = await dispatch(
      savePaymentMethod(ensureStripeCustomer(stripeCustomer), firstPaymentMethodId)
    )
      .then(response => !response?.errors)
      .catch(() => false);
  }

  dispatch(actions.placeOrdersFinished());
  return { placedOrders, failedOrder, paymentMethodSaved };
};

export default cartCheckoutPageSlice.reducer;
